import React, { useEffect, useState, useContext } from "react";
import { Modal, Radio, InputNumber, DatePicker, Spin, message, Select, Input, Button, Divider } from "antd";
import moment from "moment";
import { platformApi } from "../../../../api";
import { ContextAPI } from "../../../../ContextAPI";
import { ENDPOINT } from "../../../../constants";

const { Option } = Select;

export default function ExchangeModal({ open, close, data, onPaymentSuccess }) {
  const { loginCredintials } = useContext(ContextAPI);
  const [paymentMode, setPaymentMode] = useState("cash");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(moment());
  const [bankList, setBankList] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Local payment tracking — fetched fresh from API on open, then appended after each collect
  const [localPayments, setLocalPayments] = useState([]);
  const [fetchingPayments, setFetchingPayments] = useState(false);

  // Denomination States
  const defaultDenoms = [
    { denomination: 500, count: 0 },
    { denomination: 200, count: 0 },
    { denomination: 100, count: 0 },
    { denomination: 50, count: 0 },
    { denomination: 20, count: 0 },
    { denomination: 10, count: 0 },
    { denomination: 5, count: 0 },
    { denomination: 2, count: 0 },
    { denomination: 1, count: 0 },
  ];

  const [showDenominationModal, setShowDenominationModal] = useState(false);
  const [collectedDenominations, setCollectedDenominations] = useState([]);
  const [changeDenominations, setChangeDenominations] = useState([]);
  const [denomStep, setDenomStep] = useState(1);
  const [hasChangeToReturn, setHasChangeToReturn] = useState(true);

  const billAmount = parseFloat(data?.exchange?.vehiclePrice || 0);

  // --- Fetch fresh exchangePayments from API to avoid stale parent data ---
  const fetchFreshPayments = async () => {
    const bookingId = data?.bookingUuid || data?.id;
    if (!bookingId) return;
    try {
      setFetchingPayments(true);
      const res = await platformApi.get(`/api/booking/${bookingId}`);
      if (res?.data?.code === 200 && res?.data?.response?.code === 200) {
        const freshPayments = res.data.response.data.exchangePayments || [];
        setLocalPayments(freshPayments);

        const totalCollected = freshPayments.reduce(
          (sum, p) => sum + (parseFloat(p.collectedAmount) || 0),
          0
        );
        const pending = billAmount - totalCollected;
        setAmount("");
      }
    } catch (err) {
      console.error("Error fetching fresh exchange payments:", err);
      // Fallback to parent-passed data
      const fallbackPayments = Array.isArray(data?.exchangePayments)
        ? data.exchangePayments
        : [];
      setLocalPayments(fallbackPayments);
      const totalCollected = fallbackPayments.reduce(
        (sum, p) => sum + (parseFloat(p.collectedAmount) || 0),
        0
      );
      const pending = billAmount - totalCollected;
      setAmount("");
    } finally {
      setFetchingPayments(false);
    }
  };

  // --- Fetch branches and extract individual banks for bank transfer ---
  const fetchBranches = async () => {
    try {
      setLoading(true);
      const response = await platformApi.get("/api/company/branches");
      const branches = response?.data?.response?.data || [];
      if (response?.data?.code === 200) {
        const banks = branches.flatMap((b) =>
          (b.bankDetails || []).map((bank) => ({
            label: `${bank.name} - ${bank.accountNumber} (${b.name})`,
            value: bank.id,
            key: bank.id,
          }))
        );
        setBankList(banks);
      }
    } catch (err) {
      message.error("Error fetching bank list");
    } finally {
      setLoading(false);
    }
  };

  const setEReceipt = async (payload) => {
    // API call removed: Backend now generates the e-receipt ID internally
    return "GENERATED_BY_BACKEND";
  };

  useEffect(() => {
    if (open) {
      fetchBranches();
      setPaymentMode("cash");
      setSelectedBank(null);
      setDate(moment());
      // Always fetch fresh payments from API when modal opens
      fetchFreshPayments();
      // Reset denominations
      setCollectedDenominations(JSON.parse(JSON.stringify(defaultDenoms)));
      setChangeDenominations(JSON.parse(JSON.stringify(defaultDenoms)));
      setDenomStep(1);
      setHasChangeToReturn(true);
    }
  }, [open, data]);

  // --- Handle submit ---
  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      message.error("Please enter a valid amount");
      return;
    }
    if (paymentMode === "bank_transfer" && !selectedBank) {
      message.error("Please select a bank account for bank transfer");
      return;
    }

    if (paymentMode === "cash") {
      // Show cash denomination modal first
      setCollectedDenominations(JSON.parse(JSON.stringify(defaultDenoms)));
      setChangeDenominations(JSON.parse(JSON.stringify(defaultDenoms)));
      setDenomStep(1);
      setHasChangeToReturn(true);
      setShowDenominationModal(true);
    } else {
      // Bank Transfer submits immediately
      submitPayment();
    }
  };

  const submitPayment = async (denominationsList = []) => {
    try {
      setSubmitting(true);

      const ReceiptId = await setEReceipt({ branch: data?.branch?.id });
      if (!ReceiptId) {
        message.error("Failed to generate receipt ID");
        setSubmitting(false);
        return;
      }

      const collectedAmount = parseFloat(amount);

      const payload = {
        module: "EXCHANGE_DUE",
        moduleId: data?.bookingUuid || data?.id,
        branch: data?.branch?.id,
        billAmount,
        collectedAmount,
        changeReturned: 0,
        method: paymentMode === "bank_transfer" ? "Bank Transfer" : "CASH",
        status: "SUCCESS",
        collectedBy: loginCredintials?.id,
        customerId: data?.customer?.id || data?.customerId,
        date: date ? date.format("YYYY-MM-DD") : null,
        dateOfPayment: date ? date.toISOString() : null,
        eReceiptId: ReceiptId,
        ...(paymentMode === "bank_transfer" && selectedBank
          ? { bank: selectedBank }
          : {}),
        denominations: denominationsList,
      };

      const response = await platformApi.post("/api/payment/create", payload);
      if (response.data.code === 200) {
        message.success("Exchange Payment created successfully!");
        const paymentRecord = response.data.response?.data;
        if (paymentRecord?.id) {
          window.open(
            `${ENDPOINT}api/payment/exchange_due/receipt/${paymentRecord.id}?group=false`,
            "_blank"
          );
        }
        setSubmitting(false);
        setShowDenominationModal(false);
        // Always close after a successful payment (same as Financer Due behaviour)
        close();
        // Notify parent to refresh the table list
        if (typeof onPaymentSuccess === "function") {
          onPaymentSuccess();
        }
      } else {
        message.error("Error creating payment");
        setSubmitting(false);
      }
    } catch (err) {
      console.error("Exchange Payment Exception:", err);
      message.error("Failed to create payment");
      setSubmitting(false);
    }
  };

  // --- Denomination calculations ---
  const totalProvided = collectedDenominations.reduce(
    (sum, d) => sum + d.denomination * (parseInt(d.count) || 0),
    0
  );

  const totalChange = changeDenominations.reduce(
    (sum, d) => sum + d.denomination * (parseInt(d.count) || 0),
    0
  );

  const paymentAmount = parseFloat(amount || 0);
  const autoChange = Math.max(totalProvided - paymentAmount, 0);

  const handleDenominationSubmit = () => {
    if (hasChangeToReturn === true) {
      if (totalChange !== autoChange) {
        message.warning(
          "Change denominations do not match auto-calculated change"
        );
        return;
      }
    } else {
      setChangeDenominations(JSON.parse(JSON.stringify(defaultDenoms)));
    }

    const payloadDenoms = [
      ...collectedDenominations
        .filter((d) => d.count > 0)
        .map((d) => ({ ...d, type: "CREDIT" })),
      ...changeDenominations
        .filter((d) => d.count > 0)
        .map((d) => ({ ...d, type: "DEBIT" })),
    ];

    submitPayment(payloadDenoms);
  };

  // --- Derived summary values from local (live) state ---
  const totalCollected = localPayments.reduce(
    (sum, p) => sum + (parseFloat(p.collectedAmount) || 0),
    0
  );
  const pending = billAmount - totalCollected;
  const newPending = Math.max(pending - parseFloat(amount || 0), 0);

  return (
    <>
      <Modal
        visible={open}
        onCancel={close}
        onOk={handleSubmit}
        title="Exchange Due"
        width={500}
        okText="Collect"
        confirmLoading={submitting}
        destroyOnClose
      >
        {fetchingPayments ? (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <Spin tip="Loading payment details..." />
          </div>
        ) : (
          <>
            {/* Amount */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>
                Amount:
              </label>
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                prefix="₹"
                value={amount}
                onChange={(val) => {
                  if (val === null || val === undefined) {
                    setAmount("");
                  } else if (Number(val) >= 0) {
                    setAmount(val.toString());
                  }
                }}
                onKeyDown={(e) => {
                  if (["-", "e", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                placeholder="Enter amount"
              />
            </div>

            {/* Payment Mode */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>
                Payment Mode:
              </label>
              <Select
                value={paymentMode}
                onChange={(value) => {
                  setPaymentMode(value);
                  setSelectedBank(null);
                }}
                style={{ width: "100%" }}
                placeholder="Select Payment Mode"
              >
                <Option value="cash">Cash</Option>
                <Option value="bank_transfer">Bank Transfer</Option>
              </Select>
            </div>

            {/* Bank Account — only when Bank Transfer is selected */}
            {paymentMode === "bank_transfer" && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>
                  Payment Received Account:
                </label>
                {loading ? (
                  <div style={{ textAlign: "center", padding: "10px 0" }}>
                    <Spin tip="Fetching banks..." />
                  </div>
                ) : (
                  <Select
                    showSearch
                    placeholder="Select bank account"
                    value={selectedBank}
                    onChange={(value) => setSelectedBank(value)}
                    style={{ width: "100%" }}
                    optionFilterProp="children"
                  >
                    {bankList.map((bank) => (
                      <Option key={bank.value} value={bank.value}>
                        {bank.label}
                      </Option>
                    ))}
                  </Select>
                )}
              </div>
            )}

            {/* Receipt Date */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>
                Date:
              </label>
              <DatePicker
                value={date}
                onChange={(d) => setDate(d)}
                style={{ width: "100%" }}
                format="DD-MM-YYYY"
                disabledDate={(current) => {
                  if (!current) return false;
                  if (current > moment().endOf("day")) {
                    return true;
                  }
                  const allowPastDate = loginCredintials?.department?.allowPastDate;
                  const pastDateLimit = loginCredintials?.department?.pastDateLimit;
                  if (allowPastDate) {
                    if (pastDateLimit !== undefined && pastDateLimit !== null) {
                      const limitDate = moment().startOf("day").subtract(pastDateLimit, "days");
                      return current < limitDate;
                    }
                    return false;
                  } else {
                    return current < moment().startOf("day");
                  }
                }}
                placeholder="Select date"
              />
            </div>

            {/* Summary Panel */}
            <Divider style={{ margin: "16px 0" }} />
            <div
              style={{
                background: "#fafafa",
                borderRadius: 6,
                padding: "12px 16px",
                marginTop: 10,
                lineHeight: "2",
              }}
            >
              <div>
                <strong>Cash to be collected:</strong> ₹{billAmount.toLocaleString("en-IN")}
              </div>
              <div>
                <strong>Total Collected:</strong> ₹{totalCollected.toLocaleString("en-IN")}
              </div>
              <div>
                <strong>Pending:</strong>{" "}
                <span style={{ color: newPending > 0 ? "red" : "green" }}>
                  ₹{newPending.toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* Denomination Modal */}
      <Modal
        visible={showDenominationModal}
        title={denomStep === 1 ? "Enter Cash Provided" : "Enter Change Received"}
        onCancel={() => {
          setDenomStep(1);
          setShowDenominationModal(false);
        }}
        footer={[
          denomStep === 2 && (
            <Button key="back" onClick={() => setDenomStep(1)}>
              Back
            </Button>
          ),
          denomStep === 1 && (
            <Button
              key="next"
              type="primary"
              onClick={() => {
                if (totalProvided < paymentAmount) {
                  message.error(
                    "Cash provided cannot be less than bill amount"
                  );
                  return;
                }
                const autoChangeAmount = Math.max(totalProvided - paymentAmount, 0);
                if (autoChangeAmount === 0) {
                  // No change needed, submit immediately
                  setChangeDenominations(JSON.parse(JSON.stringify(defaultDenoms)));
                  setHasChangeToReturn(false);
                  const payloadDenoms = [
                    ...collectedDenominations
                      .filter((d) => d.count > 0)
                      .map((d) => ({ ...d, type: "CREDIT" })),
                  ];
                  submitPayment(payloadDenoms);
                } else {
                  setDenomStep(2);
                }
              }}
            >
              {Math.max(totalProvided - paymentAmount, 0) === 0 ? "Submit" : "Next"}
            </Button>
          ),
          denomStep === 2 && (
            <Button
              key="submit"
              type="primary"
              onClick={handleDenominationSubmit}
              loading={submitting}
            >
              Submit
            </Button>
          ),
        ]}
      >
        {denomStep === 1 ? (
          <>
            <h4>
              Amount to be Paid:{" "}
              <span
                style={{
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  padding: "2px 6px",
                  background: "#f9f9f9",
                  display: "inline-block",
                  minWidth: "40px",
                  textAlign: "center",
                }}
              >
                ₹{paymentAmount.toLocaleString("en-IN")}
              </span>
            </h4>

            <h4>Cash Provided</h4>
            <div style={{ maxHeight: "350px", overflowY: "auto", paddingRight: 10 }}>
              {collectedDenominations.map((d, index) => (
                <div
                  key={d.denomination}
                  style={{
                    marginBottom: 8,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <span style={{ width: 80 }}>{d.denomination} :</span>
                  <Input
                    type="number"
                    min="0"
                    value={d.count === 0 ? "" : d.count}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || /^[0-9]+$/.test(val)) {
                        const newDenoms = [...collectedDenominations];
                        newDenoms[index].count = val === "" ? "" : parseInt(val);
                        setCollectedDenominations(newDenoms);
                      }
                    }}
                    onWheel={(e) => e.target.blur()}
                    onKeyDown={(e) => {
                      if (["-", "e", "+", "."].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    style={{ width: 80, marginRight: 16 }}
                  />

                  <span>Total: ₹{(d.denomination * (d.count || 0)).toLocaleString("en-IN")}</span>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 16 }}>
              <strong>Total Provided: </strong> ₹{totalProvided.toLocaleString("en-IN")}
            </div>

            <div style={{ marginTop: 16 }}>
              <strong>Change to be Received: </strong>{" "}
              ₹{Math.max(totalProvided - paymentAmount, 0).toLocaleString("en-IN")}
            </div>
          </>
        ) : (
          <>
            <h4>Change Received</h4>

            {hasChangeToReturn && (
              <div style={{ maxHeight: "350px", overflowY: "auto", paddingRight: 10 }}>
                {changeDenominations.map((d, index) => (
                  <div
                    key={d.denomination}
                    style={{
                      marginBottom: 8,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ width: 80 }}>{d.denomination} :</span>
                    <Input
                      type="number"
                      min="0"
                      value={d.count === 0 ? "" : d.count}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^[0-9]+$/.test(val)) {
                          const newDenoms = [...changeDenominations];
                          newDenoms[index].count = val === "" ? "" : parseInt(val);
                          setChangeDenominations(newDenoms);
                        }
                      }}
                      onWheel={(e) => e.target.blur()}
                      onKeyDown={(e) => {
                        if (["-", "e", "+", "."].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      style={{ width: 80, marginRight: 16 }}
                    />
                    <span>Total: ₹{(d.denomination * (d.count || 0)).toLocaleString("en-IN")}</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              <strong>Total Change Received: </strong> ₹{totalChange.toLocaleString("en-IN")}
            </div>
            {hasChangeToReturn ? (
              <div style={{ marginTop: 16 }}>
                <strong>Expected Change: </strong> ₹{autoChange.toLocaleString("en-IN")}
              </div>
            ) : (
              <div>No change</div>
            )}
          </>
        )}
      </Modal>
    </>
  );
}
